import {
  Alert,
  AlertTitle,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Field, FieldProps, useFormikContext } from "formik";
import { useEffect } from "react";
import { RevivnApiForm } from "components/Form/RevivnApiForm";
import { SubmitAwareButton } from "components/Form/SubmitAwareButton";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { fetcher } from "services/api.service";
import { formatDate, parseTimeToAmPm } from "utils/helpers";
import { LabeledField } from "components/Form/LabeledField";
import * as Yup from "yup";
import EditAddressForm from "components/AssetRetrievals/EditAddressForm";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AssetRetrieval } from "types/assetRetrieval";
import { Location } from "types/location";

export const validationSchema = Yup.object().shape({
  employeeAddress: Yup.string().required("Address Line 1 is required."),
  employeeAddressTwo: Yup.string().nullable().optional(),
  employeeAddressCity: Yup.string().required("City is required."),
  employeeAddressState: Yup.string().required("State is required."),
  employeeAddressZipCode: Yup.string().required("Zip Code is required."),
  addressUpdateReason: Yup.string().required("Please select a reason for the address change"),
  regenerateInboundLabel: Yup.boolean().optional(),
  regenerateOutboundLabel: Yup.boolean().optional(),
  returnRecipientAddress: Yup.string().when(["location", "virtualPickup"], {
    is: (location: Location | null | undefined, virtualPickup: boolean) => {
      return !location && !virtualPickup;
    },
    then: (schema) => schema.required("Address Line 1 is required."),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  returnRecipientAddressCity: Yup.string().when(["location", "virtualPickup"], {
    is: (location: Location | null | undefined, virtualPickup: boolean) => !location && !virtualPickup,
    then: (schema) => schema.required("City is required."),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  returnRecipientAddressState: Yup.string().when(["location", "virtualPickup"], {
    is: (location: Location | null | undefined, virtualPickup: boolean) => !location && !virtualPickup,
    then: (schema) => schema.required("State is required."),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  returnRecipientAddressZipCode: Yup.string().when(["location", "virtualPickup"], {
    is: (location: Location | null | undefined, virtualPickup: boolean) => !location && !virtualPickup,
    then: (schema) => schema.required("Zip Code is required."),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  assetRetrievalDevices: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      manufacturer: Yup.string().nullable().optional(),
      model: Yup.string().nullable().optional(),
      serialNumber: Yup.string().nullable().optional(),
    }),
  ),
});

const addressChangeReasons = [
  "Incorrect employee address",
  "Incorrect company address",
  "Incorrect device details",
  "Return label missing from kit",
  "PO Box",
  "Other",
];

// Mirrors AssetRetrievalAfterUpdateLabelRegenerationService: the API only regenerates a label when these fields change.
const OUTBOUND_LABEL_FIELDS = [
  "employeeAddress",
  "employeeAddressTwo",
  "employeeAddressCity",
  "employeeAddressState",
  "employeeAddressZipCode",
];
const INBOUND_LABEL_FIELDS = [
  "returnRecipientFirstName",
  "returnRecipientLastName",
  "returnRecipientAddress",
  "returnRecipientAddressTwo",
  "returnRecipientAddressCity",
  "returnRecipientAddressState",
  "returnRecipientAddressZipCode",
];

interface RegenerateLabelsOnAddressChangeProps {
  outboundRegenerable: boolean;
  inboundRegenerable: boolean;
}

const RegenerateLabelsOnAddressChange = ({
  outboundRegenerable,
  inboundRegenerable,
}: RegenerateLabelsOnAddressChangeProps) => {
  const { values, initialValues, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const changed = (fields: string[]) => fields.some((name) => (values[name] ?? "") !== (initialValues[name] ?? ""));
  const outboundChanged = changed(OUTBOUND_LABEL_FIELDS);
  const inboundChanged = changed(INBOUND_LABEL_FIELDS);

  useEffect(() => {
    if (outboundRegenerable) setFieldValue("regenerateOutboundLabel", outboundChanged);
  }, [outboundChanged, outboundRegenerable, setFieldValue]);

  useEffect(() => {
    if (inboundRegenerable) setFieldValue("regenerateInboundLabel", inboundChanged);
  }, [inboundChanged, inboundRegenerable, setFieldValue]);

  return null;
};

interface RegenerateLabelCheckboxProps {
  name: string;
  disabled?: boolean;
  tooltip?: string;
}

const RegenerateLabelCheckbox = ({ name, disabled = false, tooltip }: RegenerateLabelCheckboxProps) => (
  <Box mt={2} mb={2}>
    <Field name={name}>
      {({ field }: FieldProps<boolean>) => (
        <Tooltip title={tooltip || ""} arrow>
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                color="primary"
                disabled={disabled}
                onChange={(e) => {
                  if (!disabled) {
                    field.onChange(e);
                  }
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Regenerate a new label
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Selected when this label's address changes
                </Typography>
              </Box>
            }
          />
        </Tooltip>
      )}
    </Field>
  </Box>
);

export function AssetRetrievalEditPage() {
  const params = useParams<{ assetRetrievalUuid: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data, mutate } = useSWR<AssetRetrieval>(
    params.assetRetrievalUuid ? `asset_retrievals/${params.assetRetrievalUuid}` : null,
    fetcher,
  );

  const refreshData = () => {
    if (mutate) {
      mutate(undefined, { revalidate: true });
    }
  };
  return (
    <Box>
      <Breadcrumbs
        data={[
          { label: "Outbound Shipping", to: "/asset_retrievals?asset_retrieval_status=open" },
          {
            label: "Edit Order Details",
            to: `/asset_retrievals?asset_retrieval_status=open/${params.assetRetrievalUuid}`,
          },
        ]}
      ></Breadcrumbs>

      <Box mt={4}>
        <Typography fontSize={20} fontWeight={700}>
          Edit Order Details
        </Typography>
      </Box>

      {data && (
        <>
          <Box
            sx={{
              mt: 2,
              py: 3,
              px: 4,
              background: "white",
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
            }}
          >
            <Typography fontSize={16} fontWeight={700}>
              Order Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                gap: 4,
                alignItems: "start",
                [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
              }}
            >
              <>
                <Stack direction="column" gap={1}>
                  <Typography variant="overline" fontSize={12} color="GrayText">
                    Order Number
                  </Typography>
                  <Typography variant="body1" fontSize={14}>
                    {data.orderNumber}
                  </Typography>
                </Stack>
                <Stack direction="column" gap={1}>
                  <Typography variant="overline" fontSize={12} color="GrayText">
                    Status
                  </Typography>
                  <Typography variant="body1" fontSize={14}>
                    {data.state}
                  </Typography>
                </Stack>
                <Stack direction="column" gap={1}>
                  <Typography variant="overline" fontSize={12} color="GrayText">
                    Date Placed
                  </Typography>
                  <Typography variant="body1" fontSize={14}>
                    {formatDate(data.createdAt)}
                  </Typography>
                </Stack>
                <Stack direction="column" gap={1}>
                  <Typography variant="overline" fontSize={12} color="GrayText">
                    Time
                  </Typography>
                  <Typography variant="body1" fontSize={14}>
                    {parseTimeToAmPm(data.createdAt)}
                  </Typography>
                </Stack>
                <Stack direction="column" gap={1}>
                  <Typography variant="overline" fontSize={12} color="GrayText">
                    Company
                  </Typography>
                  <Typography variant="body1" fontSize={14}>
                    {data.organization.name}
                  </Typography>
                </Stack>
              </>
            </Box>
          </Box>
          <Box my={4}>
            <RevivnApiForm
              initialValues={{
                ...data,
                addressChangeReasons: "",
                regenerateInboundLabel: false,
                regenerateOutboundLabel: false,
              }}
              action={`asset_retrievals/${params.assetRetrievalUuid}`}
              method="PUT"
              onSuccess={() => refreshData()}
              enableReinitialize={true}
              validationSchema={validationSchema}
            >
              <RegenerateLabelsOnAddressChange
                outboundRegenerable={data.outboundAssetRetrievalShipment?.state === "awaiting_scan"}
                inboundRegenerable={data.inboundAssetRetrievalShipment?.state === "awaiting_scan"}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  alignItems: "",
                  [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
                }}
              >
                <Box
                  sx={{
                    py: 3,
                    px: 4,
                    background: "white",
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={2}>
                    <ArrowForwardIcon />
                    <Typography fontSize={16} fontWeight={600}>
                      To (Employee) Address
                    </Typography>
                  </Stack>
                  <Typography>Location where laptop is retrieved</Typography>

                  <RegenerateLabelCheckbox
                    name="regenerateOutboundLabel"
                    disabled={
                      data.outboundAssetRetrievalShipment?.state !== "awaiting_scan" &&
                      data.outboundAssetRetrievalShipment !== null
                    }
                    tooltip={`Outbound shipment state: ${data.outboundAssetRetrievalShipment?.state}`}
                  />

                  <EditAddressForm formType="employee" />
                </Box>
                <Box
                  sx={{
                    py: 3,
                    px: 4,
                    background: "white",
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={2}>
                    <ArrowForwardIcon />
                    <Typography fontSize={16} fontWeight={600}>
                      Return Address
                    </Typography>
                  </Stack>
                  <Typography>Final destination for the retrieved laptop</Typography>

                  <RegenerateLabelCheckbox
                    name="regenerateInboundLabel"
                    disabled={
                      data.inboundAssetRetrievalShipment?.state !== "awaiting_scan" &&
                      data.inboundAssetRetrievalShipment !== null
                    }
                    tooltip={`Inbound shipment state: ${data.inboundAssetRetrievalShipment?.state}`}
                  />

                  {data.location && data.location.id ? (
                    <Box mt={2} sx={{ display: "flex", flexDirection: "column", gap: 2, alignContent: "center" }}>
                      <Alert severity="info">
                        <AlertTitle
                          sx={{ fontWeight: 600 }}
                        >{`This box is going back to ${data.virtualPickup ? "Revivn" : "Clients"} Office `}</AlertTitle>

                        <Box>
                          <Typography>{data.location.address}</Typography>
                          <Typography>{data.location.address2}</Typography>
                          <Typography>{data.location.city}</Typography>
                          <Typography>{data.location.zipcode}</Typography>
                          <Typography>{data.location.country}</Typography>
                        </Box>
                      </Alert>
                      <Box sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2 }}>
                        <LabeledField name="returnRecipientFirstName" label="First Name" />
                        <LabeledField name="returnRecipientLastName" label="Last Name" />
                      </Box>
                      <LabeledField name="returnRecipientEmail" label="Email" />
                    </Box>
                  ) : (
                    <EditAddressForm formType="returnRecipient" />
                  )}
                </Box>

                <Box
                  sx={{
                    py: 3,
                    px: 4,
                    background: "white",
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={2}>
                    <ArrowForwardIcon />
                    <Typography fontSize={16} fontWeight={600}>
                      Devices
                    </Typography>
                  </Stack>
                  <Typography>Devices to be retrieved from the employee</Typography>

                  {data.assetRetrievalDevices.map((device, index) => (
                    <Box
                      key={device.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 2,
                        mt: 2,
                        [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
                      }}
                    >
                      <LabeledField name={`assetRetrievalDevices.${index}.manufacturer`} label="Manufacturer" />
                      <LabeledField name={`assetRetrievalDevices.${index}.model`} label="Model" />
                      <LabeledField name={`assetRetrievalDevices.${index}.serialNumber`} label="Serial Number" />
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  py: 3,
                  px: 4,
                  my: 4,
                  background: "white",
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
                }}
              >
                <Stack direction="column" gap={2} alignItems="left">
                  <Typography fontSize={16} fontWeight={600}>
                    Reason for Address Change *
                  </Typography>
                  <Typography fontSize={14} fontWeight={400}>
                    Select the reason you are updating details for this order
                  </Typography>
                  <Field as={TextField} id="outlined-select-currency" select size="small" name="addressUpdateReason">
                    {addressChangeReasons.map((option, i) => (
                      <MenuItem key={i} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Field>
                </Stack>
              </Box>

              <Stack direction="row" gap={2} alignItems="left">
                <SubmitAwareButton type="submit" variant="contained">
                  Save
                </SubmitAwareButton>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Stack>
            </RevivnApiForm>
          </Box>
        </>
      )}
    </Box>
  );
}
